import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ChevronLeft, History, Smartphone } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { fetchLoginHistory, type LoginEntry } from './loginHistoryApi'

/**
 * A3.11 — Kirish tarixi. Akkauntning TO'LIQ qurilma/login izi (bekor qilingan
 * qurilmalar ham) — A3.10 aktiv sessiyalardan farqli xavfsizlik-audit ko'rinishi.
 * IP saqlangani ko'rsatiladi, lekin proksi-shlyuz IP bo'lishi mumkin (zaif signal);
 * qurilma/platforma/versiya ishonchli ustunlar. Read-only. Matnlar lokal.
 */

const S = {
  title: 'Kirish tarixi',
  back: 'Karta',
  empty: 'Kirish yozuvi yo‘q',
  device: 'Qurilma',
  platform: 'Platforma',
  version: 'Versiya',
  ip: 'IP',
  firstLogin: 'Birinchi kirish',
  lastActive: 'Oxirgi faollik',
  status: 'Holat',
  active: 'Faol',
  revoked: 'Bekor qilingan',
  ipNote: 'IP ba’zан proksi-shlyuz manzili bo‘lishi mumkin — zaif signal.',
}

function fmtTime(ts?: number): string {
  if (!ts || !Number.isFinite(ts)) return '—'
  try {
    return new Date(ts).toLocaleString()
  } catch {
    return '—'
  }
}

export function UserLoginHistory() {
  const { id = '' } = useParams()
  const userId = Number(id)
  const [rows, setRows] = useState<LoginEntry[] | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    fetchLoginHistory(userId, ac.signal)
      .then((r) => alive && setRows(r))
      .catch(() => alive && setRows([]))
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
      ac.abort()
    }
  }, [userId])

  return (
    <>
      <PageHeader
        title={S.title}
        screenId="A3.11"
        actions={
          <Link
            to={`/users/${id}`}
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {S.back}
          </Link>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {loading || rows === null ? (
          <div className="p-6 text-center text-sm text-tertiary">…</div>
        ) : rows.length === 0 ? (
          <div className="px-3 py-10 text-center text-sm text-tertiary">
            <History size={24} className="mx-auto mb-2 opacity-60" />
            {S.empty}
          </div>
        ) : (
          <div className="space-y-2">
            <div className="text-xs text-tertiary">{S.ipNote}</div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[820px] text-sm">
                <thead>
                  <tr className="border-b border-border-default text-left text-xs text-tertiary">
                    <th className="px-2 py-1.5 font-medium">{S.device}</th>
                    <th className="px-2 py-1.5 font-medium">{S.platform}</th>
                    <th className="px-2 py-1.5 font-medium">{S.version}</th>
                    <th className="px-2 py-1.5 font-medium">{S.ip}</th>
                    <th className="px-2 py-1.5 font-medium">{S.firstLogin}</th>
                    <th className="px-2 py-1.5 font-medium">{S.lastActive}</th>
                    <th className="px-2 py-1.5 font-medium">{S.status}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-b border-border-subtle align-top">
                      <td className="px-2 py-1.5">
                        <span className="flex items-center gap-1.5 text-secondary">
                          <Smartphone size={13} className="text-tertiary" />
                          {r.device_model || r.device_uid.slice(0, 8)}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 text-secondary">{r.platform || '—'}</td>
                      <td className="px-2 py-1.5 font-mono text-tertiary">{r.app_version || '—'}</td>
                      <td className="px-2 py-1.5 font-mono text-tertiary">{r.ip || '—'}</td>
                      <td className="whitespace-nowrap px-2 py-1.5 text-tertiary">{fmtTime(r.first_login_at)}</td>
                      <td className="whitespace-nowrap px-2 py-1.5 text-tertiary">{fmtTime(r.last_active_at)}</td>
                      <td className="px-2 py-1.5">
                        {r.revoked ? (
                          <span className="inline-flex items-center rounded-full border border-border-default bg-surface-2 px-2 py-0.5 text-xs text-tertiary">
                            {S.revoked}
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-xs font-medium text-emerald-400">
                            {S.active}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </>
  )
}
